import { useEffect, useRef, useState } from 'react';
import { Link } from '@tanstack/react-router';
import type { ImageRef, TextSectionContent } from '~/content/types';
import { Container, DigitReel, Grid, ResponsiveText, SmartImage } from '~/components/ui';
import { Section } from '~/components/layout';
import { cn } from '~/lib/cn';
import { useLocalizedPath } from '~/lib/useLanguage';

// The figure that leads the "Trusted by 500k+ …" heading. Only these three
// digits roll up in their own odometer reels; the "k+" suffix and the rest of
// the copy stay static.
const COUNT_UP_TOKEN = '500';

/**
 * Renders the title with the "500" rolling up across three odometer reels while
 * everything around it stays static. Mirrors the CtaBanner reuse pattern: an
 * sr-only static value for assistive tech and decorative, aria-hidden reels.
 */
function renderCountUpTitle(title: string, play: boolean) {
  const idx = title.indexOf(COUNT_UP_TOKEN);
  const prefix = title.slice(0, idx);
  const suffix = title.slice(idx + COUNT_UP_TOKEN.length);
  const digits = COUNT_UP_TOKEN.split('').map(Number);
  return (
    <>
      {prefix}
      {/* Static value for assistive tech; the reels are decorative. */}
      <span className="sr-only">{COUNT_UP_TOKEN}</span>
      {/* Each reel is its own inline element, so in an RTL page they would flow
          right-to-left and render "500" as "005". Pinning the group to LTR keeps
          the figure readable in Arabic without affecting the LTR layout. */}
      <span dir="ltr" className="inline-flex">
        {digits.map((digit, i) => (
          <DigitReel key={i} target={digit} play={play} />
        ))}
      </span>
      {suffix}
    </>
  );
}

/**
 * Right-aligned heading + paragraph pattern used for "Trusted by 500k+" and
 * "The safe space for your money" sections.
 *
 * When `publications` is provided, the section renders the "Trusted by" variant:
 * full-width top/bottom borders (aligned to the content grid) with a press-logo
 * strip below the copy.
 */
export function TextSection({
  content,
  sectionClassName,
  image,
}: {
  content: TextSectionContent;
  /** Extra classes for the outer section wrapper (e.g. per-instance spacing overrides). */
  sectionClassName?: string;
  /**
   * Optional image for the empty left half of the bordered variant, whose copy
   * sits in cols 6–9. Opt-in per instance rather than on the content object,
   * because this section is shared by the homepage, /business, /stablecoin,
   * /compare and every send-money page, and only some of them want it filled.
   * Ignored by the plain variant, which has no spare column.
   */
  image?: ImageRef;
}) {
  // Only the opted-in "Trusted by 500k+" heading rolls its number; every other
  // TextSection renders its title as plain static text.
  const headingRef = useRef<HTMLHeadingElement>(null);
  const localizedPathFor = useLocalizedPath();
  const [play, setPlay] = useState(false);
  const countUp = Boolean(content.countUpHeading) && content.title.includes(COUNT_UP_TOKEN);

  useEffect(() => {
    if (!countUp) return;
    const el = headingRef.current;
    if (!el) return;
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

    // This heading is usually below the fold, so gate the roll on
    // scroll-into-view (same pattern/threshold as CtaBanner).
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((e) => e.isIntersecting)) {
          setPlay(true);
          observer.disconnect();
        }
      },
      { threshold: 0.4 },
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [countUp]);

  const titleNode = countUp ? renderCountUpTitle(content.title, play) : content.title;

  if (content.publications || content.bordered) {
    return (
      <section className={cn('my-20', sectionClassName)}>
        <Container>
          {/* Content sits in grid columns 6–9 so it lines up with the FeatureBlock
              text column below. Only the "Trusted by" variant is a framed band and
              keeps its rules; the plain bordered variant draws none — the sections
              on either side already open with their own heading rule, so a band rule
              just doubles up. `bordered` still selects this wider layout. */}
          <div className={cn('py-24', content.publications && 'border-y border-black-6')}>
            <Grid className="items-center">
              {image ? (
                <div className="col-span-9 overflow-hidden lg:col-span-4 lg:col-start-1 lg:row-start-1">
                  {/* Same sizing as FeatureBlock's image — square on mobile, a fixed
                      720px on desktop — so this block and the app-download one above
                      it read as the same treatment rather than two different heights
                      down the same edge. */}
                  <SmartImage
                    image={image}
                    className="aspect-square w-full lg:aspect-auto lg:h-[720px]"
                  />
                </div>
              ) : null}
              <div className="col-span-9 lg:col-span-4 lg:col-start-6 lg:row-start-1">
                <div className="border-b border-black-6 pb-6">
                  <h2
                    ref={headingRef}
                    className="text-h4 font-semibold tracking-[-0.02em] whitespace-pre-line md:text-h3">
                    {titleNode}
                  </h2>
                </div>
                <p className="mt-8 text-body text-grey-900">
                  <ResponsiveText text={content.body} mobileText={content.mobileBody} />
                </p>
                {content.link && (
                  <Link
                    to={localizedPathFor(content.link.href)}
                    className="mt-6 inline-block text-body-m font-medium text-red-500 hover:text-red-700">
                    {content.link.label}
                  </Link>
                )}
                {content.publications && (
                  <img
                    src={content.publications.src}
                    alt={content.publications.alt}
                    loading="lazy"
                    decoding="async"
                    className="mt-12 w-full max-w-[400px] object-contain"
                  />
                )}
              </div>
            </Grid>
          </div>
        </Container>
      </section>
    );
  }

  return (
    <Section className={sectionClassName}>
      <div className="lg:ms-auto lg:max-w-[608px]">
        <div className="border-b border-black-6 pb-6">
          <h2
            ref={headingRef}
            className="text-h4 font-semibold tracking-[-0.02em] text-balance md:text-h3">
            {titleNode}
          </h2>
        </div>
        <p className="mt-8 max-w-[492px] text-body text-grey-900">
          <ResponsiveText text={content.body} mobileText={content.mobileBody} />
        </p>
        {content.link && (
          <Link
            to={localizedPathFor(content.link.href)}
            className="mt-6 inline-block text-body-m font-medium text-red-500 hover:text-red-700">
            {content.link.label}
          </Link>
        )}
      </div>
    </Section>
  );
}
